import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { Province } from "../../types";
import { FinancingRatesInterface } from "../../types/finance";

type Price<T> = T | null;

export type Duration = { month: number, rate: number };

export type Frequency = { value: number, text: string };

export type PriceStore<T> = (data : T | null) => void;

interface CalculatorFinancialInterface {
    financingRates : FinancingRatesInterface | null,
    setFinancingRates: PriceStore<FinancingRatesInterface>,

    vehiclePrice: Price<number>,
    setVehiclePrice: PriceStore<Price<number>>,

    salesTax: Price<number>
    setSalesTax: PriceStore<Price<number>>,

    includeTax: Price<boolean>,
    setIncludeTax: () => void

    duration: Duration | null,
    setDuration: (duration : Duration | null) => void,

    frequency: Frequency | null,
    setFrequency: (frequency : Frequency | null) => void,

    calculatedValue : Price<number>,
    setCalculatedValue: () => void,

    paymentAmount: Price<number>,
    setPaymentAmount: PriceStore<Price<number>>,

    downpayment : Price<number>,
    setDownpayment: PriceStore<Price<number>>,

    CORE : {
        get_amount_financed : () => number,
        get_finance: (APR: number, frequency: number, term: number, do_not_round: boolean) => number,
        get_monthly_payment : () => number,
        get_finance_obligation : (province: Province) => number,
        get_finance_cost_of_borrowing : () => number,
    }
}

export const useCalculatorFinancial = create<CalculatorFinancialInterface>()(devtools((set, get) => ({
    financingRates : null,
    setFinancingRates: (rates) => set(() => ({ financingRates: rates })),

    vehiclePrice: null, setVehiclePrice : (price) => set(() => ({vehiclePrice : price})),

    salesTax: null, setSalesTax: (price) => set(() => ({ salesTax: price })),

    duration: null,
    setDuration: (duration) => set((state) => {
        state.duration = duration;
        state.setCalculatedValue();
        return { duration };
    }),
    frequency: null,
    setFrequency: (frequency) => set((state) => {
        state.frequency = frequency;
        state.setCalculatedValue();
        return { frequency };
    }),
    includeTax: false, setIncludeTax: () => set((state) => {
        const tax = get().includeTax;
        state.includeTax = !tax;
        state.setCalculatedValue();
        return { includeTax: !tax }
    }),

    downpayment: null,
    setDownpayment: (price) => set((state) => {
        state.downpayment = price;
        state.setCalculatedValue();
        return { downpayment: price };
    }),

    paymentAmount: null, setPaymentAmount: (price) => set(() => ({ paymentAmount: price })),

    calculatedValue : 0,
    setCalculatedValue: () => {
        let calculatedValue = 0;

        const includeTax = get().includeTax;
        const salesTax =  get().salesTax;
        const vehiclePrice = get().vehiclePrice;

        const rate = Number(get().duration?.rate);
        const months = Number(get().duration?.month);
        const frequency = get().frequency?.value;

        let paymentAmount = 0;

        if(includeTax) {
            if (salesTax && vehiclePrice) {
                calculatedValue = vehiclePrice * (1 + salesTax);
            }
        }
        else {
            calculatedValue = vehiclePrice as number;
        }

        if(rate && months) {
            paymentAmount = get().CORE.get_finance(Number(rate), Number(frequency), Number(months), false);
        }

        set(() => ({ calculatedValue, paymentAmount }));
    },

    CORE : {
        get_amount_financed : () => {
            let amountFinanced = 0;

            const includeTax = get().includeTax;
            const salesTax = get().salesTax;
            const vehiclePrice = get().vehiclePrice;
            const downpayment = get().downpayment != null ? get().downpayment : 0;

            if(salesTax && vehiclePrice) {
                const sales_tax = includeTax === true ? Number(salesTax) : 0;

                amountFinanced = (Number(vehiclePrice) * (1 + sales_tax)) - Number(downpayment);
            }

            return amountFinanced;
        },
        get_finance : (APR : number, frequency : number, term : number, do_not_round : boolean) => {
            let final_amount = 0;

            const amount_financed = get().CORE.get_amount_financed();

            if (APR == 0) {
                if(amount_financed && term && frequency) {
                    final_amount = amount_financed / term * 12 / frequency;
                }
            } else {
                if(APR && term && frequency) {
                    final_amount = (amount_financed * ((APR / 100 / 12) / (1 - Math.pow(1 + (APR / 100 / 12), - term)))) * 12 / frequency;
                }
            }

            return do_not_round === true ? final_amount : Math.round(final_amount);
        },
        get_monthly_payment : () => {
            let monthly_payment = 0;
            const rate = get().duration?.rate;
            const months = get().duration?.month;

            monthly_payment = get().CORE.get_finance(rate as number, 12, months as number, true);
            return monthly_payment;
        },

        get_finance_obligation : (province: Province) => {
            let finance_obligation = 0;
            const prov = province;
            const additional_fees = prov == 'QC' ? 46 : 0;
            const monthly_payment = get().CORE.get_monthly_payment();
            const months = get().duration?.month;
            const down_payment = get().downpayment;

            if(monthly_payment && months && down_payment) {
                finance_obligation = (monthly_payment * months) + down_payment + additional_fees;
            }

            return finance_obligation;
        },

        get_finance_cost_of_borrowing : () => {
            let finance_cost_of_borrowing = 0;
            const monthly_payment = get().CORE.get_monthly_payment();
            const months = get().duration?.month;
            const amount_financed = get().CORE.get_amount_financed();

            if(months) {
                finance_cost_of_borrowing = (monthly_payment * months) - amount_financed;
            }

            return finance_cost_of_borrowing;
        }
    }
})));
